import {css, StyleSheet} from 'aphrodite';
import {produce} from 'immer';
import React, {useMemo, useState} from 'react';
import isEqual from 'react-fast-compare';

import {assert, assertAndContinue} from 'Asserts';
import {LookupConfigDescription} from 'lookup-config/LookupConfigDescription';
import {LookupConfigSpec} from 'lookup-config/LookupConfigModalConstants';
import SimpleTableEditable, {standaloneProps} from 'notebook/simple-tables/SimpleTableEditable';
import {TableDimension} from 'notebook/tables/constants/TableSelectionConstants';
import SimpleTableAxisCell from 'notebook/tables/core-ui/SimpleTableAxisCell';
import {
  ColumnConstraint,
  Row,
  Table,
  TableColumnType,
  TableHeaderControlsArgs,
  TooltipIcon,
} from 'notebook/tables/TableConstants';
import {isLookupConfigInfoType, isPlateLayoutColumnType} from 'notebook/tables/typed-values/DataTypes';
import {BaseTypedValueType} from 'notebook/tables/typed-values/TypedValueConstants';
import {
  LookupConfigInfoTypedValue,
  LookupConfigTypedValue,
  PlateLayoutTypedValue,
} from 'notebook/tables/typed-values/TypedValueTypes';
import notifications from 'notifications';
import {Schema} from 'schemas/SchemaConstants';
import {ButtonStyle} from 'ui-library/buttons/Button';
import legacyRunModal from 'ui-library/legacyRunModal';

import {AutomationInputFileConfigEditorProps} from '../AutomationFileConfigUtils';
import {IconButton} from '../RunSchemaEditorActionButton';
import {AutomationInputFileConfigSpec, PlateLayoutType, RowConfigSpec} from '../RunSchemaSpecConstants';
import {LookupConfigContext} from './LookupConfigContext';
import SortColumnsModal from './SortColumnsModal';

export const DESTINATION_COLUMN = 'Destination ';
export const PLATE_LAYOUT_COLUMN = 'Plate Layout ';
export const NO_DESTINATION = 'None';
export const PROTECTED_COLUMN_NAMES = new Set([DESTINATION_COLUMN, PLATE_LAYOUT_COLUMN]);

// exported for test
export const DATA_TEST_ATTRS = {
  sortColumns: {component: 'ColumnsMapEditor', element: 'sortColumns'},
};

export const FAKE_COLUMN_ICON: TooltipIcon = {
  icon: 'eye-slash',
  tooltip: "This column won't show up in your input file",
};

function _createTableRowForRowConfig(
  rowConfig: RowConfigSpec,
  columns: Array<string>,
  destinationInfos: {[destinationName: string]: {lookupConfig: LookupConfigSpec}} | undefined
): Row {
  const _getTypedValue = (lookup: LookupConfigSpec): LookupConfigTypedValue =>
    lookup as LookupConfigTypedValue;
  return columns.map((column) => {
    if (column === DESTINATION_COLUMN) {
      return {
        text: rowConfig.destination || NO_DESTINATION,
      };
    } else if (column === PLATE_LAYOUT_COLUMN) {
      const plateLayoutTypedValue: PlateLayoutTypedValue = {
        type: BaseTypedValueType.PLATE_LAYOUT,
        plateLayout: rowConfig.plateLayout != null ? rowConfig.plateLayout.layoutType : null,
        source: _getTypedValue(rowConfig.source),
        destination:
          destinationInfos && rowConfig.destination
            ? _getTypedValue(destinationInfos[rowConfig.destination].lookupConfig)
            : null,
      };
      return {text: '', typedValue: plateLayoutTypedValue};
    }

    const lookupConfigInfo: LookupConfigInfoTypedValue = {
      type: BaseTypedValueType.LOOKUP_CONFIG_INFO,
      lookupConfig:
        rowConfig.columnsMap[column] != null ? _getTypedValue(rowConfig.columnsMap[column]) : null,
      source: _getTypedValue(rowConfig.source),
      destination:
        destinationInfos && rowConfig.destination
          ? _getTypedValue(destinationInfos[rowConfig.destination].lookupConfig)
          : null,
      usePlateLayout: rowConfig.plateLayout != null,
    };

    return {
      text: '',
      typedValue: lookupConfigInfo,
    };
  });
}

function _getColumnConstraints(
  inputFileConfig: AutomationInputFileConfigSpec,
  runSchema: Schema | null
): Array<ColumnConstraint> {
  const possibleDestinations = [NO_DESTINATION];
  possibleDestinations.push(...Object.keys(inputFileConfig.destinationInfos || {}));

  const constraints: Array<ColumnConstraint> = [
    {
      viewer: true,
      columnDefinition: {
        type: TableColumnType.OPTION_SELECTOR,
        options: possibleDestinations,
        isRequired: false,
      },
      tooltipIcons: [FAKE_COLUMN_ICON],
    },
    {
      viewer: true,
      columnDefinition: {
        type: TableColumnType.PLATE_LAYOUT_SELECTOR,
        runSchema,
        isRequired: false,
      },
      tooltipIcons: [FAKE_COLUMN_ICON],
    },
  ];
  for (const _ of inputFileConfig.columns) {
    constraints.push({
      columnDefinition: {
        type: TableColumnType.LOOKUP_CONFIG,
        isRequired: false,
      },
    });
  }
  return constraints;
}

function _createColumnsMapTable(
  inputFileConfig: AutomationInputFileConfigSpec,
  runSchema: Schema | null,
  columnWidths: Array<number>
): Table {
  const columns = [DESTINATION_COLUMN, PLATE_LAYOUT_COLUMN];
  columns.push(...inputFileConfig.columns);
  return {
    name: 'Input table',
    table: inputFileConfig.rowConfigs.map((rowConfig) =>
      _createTableRowForRowConfig(rowConfig, columns, inputFileConfig.destinationInfos)
    ),
    widths: columnWidths,
    columnConstraints: _getColumnConstraints(inputFileConfig, runSchema),
    headerRow: false,
    headerCol: false,
    columnLabels: columns,
    rowLabels: inputFileConfig.rowConfigs.map((rowConfig) => (
      <LookupConfigDescription baseSchema={runSchema} lookupConfig={rowConfig.source} />
    )),
    _rowHeader: (
      <SimpleTableAxisCell
        key="source"
        index={0}
        dim={TableDimension.COLUMN}
        label="Source"
        numDims={columns.length + 1}
        tooltipIcons={[FAKE_COLUMN_ICON]}
        isLeftAlign={true}
      />
    ),
    _firstColumnWidth: 180,
    lockedDims: true,
    allowColumnEdits: true,
    legacyFirstEditableColumnIndex: PROTECTED_COLUMN_NAMES.size,
    _disableInsertColsTooltip: `Columns may be inserted to the right of ${PLATE_LAYOUT_COLUMN}`,
    _hideRename: true,
  };
}

export function setLookupConfig(
  inputFileConfig: AutomationInputFileConfigSpec,
  rowIdx: number,
  columnIdx: number,
  lookupConfig: LookupConfigSpec | null
): AutomationInputFileConfigSpec {
  assert(Number.isInteger(rowIdx), 'Selected row index must be an integer');
  assert(Number.isInteger(columnIdx), 'Selected column index must be an integer');
  assert(rowIdx >= 0 && rowIdx < inputFileConfig.rowConfigs.length, 'Selected row must match a row config');
  assert(
    columnIdx >= 0 && columnIdx < inputFileConfig.columns.length,
    'Selected column must match an input file column'
  );

  const column = inputFileConfig.columns[columnIdx];
  if (lookupConfig != null) {
    inputFileConfig.rowConfigs[rowIdx].columnsMap[column] = lookupConfig;
  } else {
    delete inputFileConfig.rowConfigs[rowIdx].columnsMap[column];
  }

  return inputFileConfig;
}

export function unsetLookupConfig(
  inputFileConfig: AutomationInputFileConfigSpec,
  rowIdx: number,
  columnIdx: number
): AutomationInputFileConfigSpec {
  return setLookupConfig(inputFileConfig, rowIdx, columnIdx, null);
}

function _getProcessedColumns(columns: Array<string | null>): Array<string> {
  const processedColumns: Array<string> = [];
  let numDuplicateNewCols = 0;
  const newColumnPattern = /^New Column( \d+)?$/;
  columns.forEach((columnLabel) => {
    const match = columnLabel != null ? columnLabel?.match(newColumnPattern) : null;
    if (match) {
      const dupNum = match[1] != null ? parseInt(match[1], 10) : 0;
      numDuplicateNewCols = Math.max(numDuplicateNewCols, dupNum) + 1;
    }
  });
  columns.forEach((columnLabel) => {
    let value;
    if (columnLabel == null) {
      value = numDuplicateNewCols > 0 ? `New Column ${numDuplicateNewCols}` : 'New Column';
      numDuplicateNewCols++;
    } else {
      value = columnLabel;
    }
    processedColumns.push(value);
  });
  return processedColumns;
}

function _removeColumn(
  inputFileConfig: AutomationInputFileConfigSpec,
  newColumns: Array<string | null>
): void {
  const columnsSet = new Set(newColumns);
  const removedColumns = inputFileConfig.columns.filter((column: string) => !columnsSet.has(column));
  for (const rowConfig of inputFileConfig.rowConfigs) {
    for (const columnToRemove of removedColumns) {
      if (rowConfig.columnsMap[columnToRemove] != null) {
        delete rowConfig.columnsMap[columnToRemove];
      }
    }
  }
  inputFileConfig.sortColumns = inputFileConfig.sortColumns.filter((column: string) =>
    columnsSet.has(column)
  );
}

function _buildRenameColumnsMap(
  inputFileConfig: AutomationInputFileConfigSpec,
  newColumns: Array<string | null>
): {[oldName: string]: string} {
  const renamedColumnsMap: {[oldName: string]: string} = {};
  const takenColumnNames = new Set(inputFileConfig.columns);
  for (let idx = 0; idx < inputFileConfig.columns.length; idx++) {
    const column = inputFileConfig.columns[idx];
    const displayIdx = idx + PROTECTED_COLUMN_NAMES.size + 1;
    if (column !== newColumns[idx]) {
      if (takenColumnNames.has(newColumns[idx]!)) {
        notifications.error(
          `Cannot change column ${displayIdx} name. A column with the name "${newColumns[
            idx
          ]!}" already exists.`
        );
        return {};
      } else if (PROTECTED_COLUMN_NAMES.has(newColumns[idx]!)) {
        notifications.error(
          `Cannot change column ${displayIdx} name. "${newColumns[idx]!}" is a protected column name.`
        );
        return {};
      }
      renamedColumnsMap[column] = newColumns[idx]!;
    }
  }
  return renamedColumnsMap;
}

function _renameColumn(
  inputFileConfig: AutomationInputFileConfigSpec,
  newColumns: Array<string | null>
): Array<string> {
  const renamedColumnsMap = _buildRenameColumnsMap(inputFileConfig, newColumns);
  for (const oldColumn of Object.keys(renamedColumnsMap)) {
    for (const rowConfig of inputFileConfig.rowConfigs) {
      if (rowConfig.columnsMap[oldColumn] != null) {
        rowConfig.columnsMap[renamedColumnsMap[oldColumn]] = {...rowConfig.columnsMap[oldColumn]};
        delete rowConfig.columnsMap[oldColumn];
      }
    }
  }
  inputFileConfig.sortColumns = inputFileConfig.sortColumns.map(
    (sortColumnLabel) => renamedColumnsMap[sortColumnLabel] || sortColumnLabel
  );
  return inputFileConfig.columns.map((columnLabel) => renamedColumnsMap[columnLabel] || columnLabel);
}

function _setConfigsFromTable(inputFileConfig: AutomationInputFileConfigSpec, table: Table): void {
  table.table.forEach((row, rowIdx) => {
    row.forEach((cell, columnIdx) => {
      if (columnIdx === 0) {
        const selectedDestination = row[columnIdx].text !== NO_DESTINATION ? row[columnIdx].text : undefined;
        if (selectedDestination !== undefined) {
          assertAndContinue(
            inputFileConfig.destinationInfos !== undefined &&
              inputFileConfig.destinationInfos[selectedDestination] !== undefined,
            'Selected destination must be "None" or one of the already-defined destinations.'
          );
        }
        if (selectedDestination == null) {
          delete inputFileConfig.rowConfigs[rowIdx].destination;
        } else if (selectedDestination !== inputFileConfig.rowConfigs[rowIdx].destination) {
          inputFileConfig.rowConfigs[rowIdx].destination = selectedDestination;
        }
      } else if (columnIdx === 1) {
        const plateLayoutTypedValue = row[columnIdx].typedValue!;
        if (!isPlateLayoutColumnType(plateLayoutTypedValue)) {
          assertAndContinue(false, 'Expected plate layout cell to be PlateLayoutTypedValue');
          return;
        }
        const selectedPlateLayoutType = plateLayoutTypedValue.plateLayout as PlateLayoutType;
        if (selectedPlateLayoutType == null) {
          delete inputFileConfig.rowConfigs[rowIdx].plateLayout;
        } else {
          if (
            inputFileConfig.rowConfigs[rowIdx].plateLayout != null &&
            inputFileConfig.rowConfigs[rowIdx].plateLayout.layoutType === selectedPlateLayoutType
          ) {
            return;
          }
          inputFileConfig.rowConfigs[rowIdx].plateLayout = {
            layoutType: selectedPlateLayoutType,
          };
        }
      } else {
        const columnIds = columnIdx - PROTECTED_COLUMN_NAMES.size;
        const column = inputFileConfig.columns[columnIds];
        const baseLookup = inputFileConfig.rowConfigs[rowIdx].columnsMap[column] || null;
        const typedValue = cell.typedValue || null;
        if (typedValue != null && !isLookupConfigInfoType(typedValue)) {
          assertAndContinue(false, 'Expected lookup config cell to be LookupConfigInfoTypedValue or null');
          return;
        }
        const currLookup =
          typedValue != null && typedValue.lookupConfig != null
            ? (typedValue.lookupConfig as LookupConfigSpec)
            : null;
        if (!isEqual(baseLookup, currLookup)) {
          if (currLookup != null) {
            inputFileConfig = setLookupConfig(inputFileConfig, rowIdx, columnIds, currLookup);
          } else {
            inputFileConfig = unsetLookupConfig(inputFileConfig, rowIdx, columnIds);
          }
        }
      }
    });
  });
}

export function getUpdatedInputFileConfigFromTable(
  inputFileConfig: AutomationInputFileConfigSpec,
  table: Table
): AutomationInputFileConfigSpec {
  let columnLabels = table.columnLabels;
  PROTECTED_COLUMN_NAMES.forEach((column) => {
    const columnIdx = columnLabels.indexOf(column);
    columnLabels.splice(columnIdx, 1);
  });
  const oldColumnLabels = inputFileConfig.columns;
  const isColumnAdded = columnLabels.length > oldColumnLabels.length;
  const isColumnRemoved = columnLabels.length < oldColumnLabels.length;
  if (isColumnRemoved) {
    _removeColumn(inputFileConfig, columnLabels);
  } else if (!isColumnAdded) {
    if (!isEqual(columnLabels, oldColumnLabels)) {
      // the column labels post rename may not be equal to the user provided values if the user provided invalid values
      columnLabels = _renameColumn(inputFileConfig, columnLabels);
    } else {
      _setConfigsFromTable(inputFileConfig, table);
    }
  }
  inputFileConfig.columns = _getProcessedColumns(columnLabels);
  return inputFileConfig;
}

export default function ColumnsMapEditor(props: AutomationInputFileConfigEditorProps): JSX.Element | null {
  const lookupConfigContext = useMemo(
    () => ({
      baseSchema: props.baseSchema,
    }),
    [props.baseSchema]
  );
  const defaultColumnWidths: Array<number> = [];
  PROTECTED_COLUMN_NAMES.forEach(() => {
    defaultColumnWidths.push(180);
  });
  defaultColumnWidths.push(...props.inputFileConfig.columns.map(() => 180));
  const [columnWidths, setColumnWidths] = useState(defaultColumnWidths);
  const [tableIsValid, setTableIsValid] = useState(true);

  const renderHeaderControls = ({isCollapsed}: TableHeaderControlsArgs): JSX.Element | null => {
    if (isCollapsed) {
      return null;
    }
    return (
      <IconButton
        buttonStyle={
          props.inputFileConfig.sortColumns.length === 0
            ? ButtonStyle.TERTIARY_GRAY
            : ButtonStyle.TERTIARY_BLUE_POWDER
        }
        icon="sort-column"
        disabled={props.inputFileConfig.columns.length === 0}
        label="Configure columns to sort the input file by"
        onClick={() => {
          legacyRunModal(SortColumnsModal, {
            initialColumns: props.inputFileConfig.sortColumns,
            possibleColumns: props.inputFileConfig.columns,
            onRequestSubmit: (updatedSortColumns: Array<string>) => {
              props.onUpdateConfig({
                ...props.inputFileConfig,
                sortColumns: updatedSortColumns,
              });
            },
          });
        }}
        dataTestAttrs={DATA_TEST_ATTRS.sortColumns}
      />
    );
  };

  if (!props.inputFileConfig.rowConfigs.length) {
    return null;
  }

  return (
    <div className={`simpleDivTable-row mediocre ${css(styles.columnTableContainer, styles.divTableRow)}`}>
      <LookupConfigContext.Provider value={lookupConfigContext}>
        <SimpleTableEditable
          {...standaloneProps}
          table={_createColumnsMapTable(props.inputFileConfig, props.baseSchema, columnWidths)}
          cid={`input-config-columns-${props.index}`}
          isLeftAlignColumnHeader={true}
          isLeftAlignRowHeader={true}
          hideFormulaBox={true}
          isCompactBottomSpacing={true}
          locked={false}
          updateHandler={(_idx, {table}) => {
            setColumnWidths(table.widths);
            const inputFileConfig = produce(props.inputFileConfig, (draftInputFileConfig) =>
              getUpdatedInputFileConfigFromTable(draftInputFileConfig, table)
            );
            props.onUpdateConfig(inputFileConfig);
          }}
          onComputedTableChanged={(computedTable) => {
            const isValid = computedTable.flat().every(({error}) => error == null);
            if (isValid !== tableIsValid) {
              setTableIsValid(isValid);
              props.onValidationChange(isValid);
            }
          }}
          storeState={() => {}}
          headerControls={renderHeaderControls}
        />
      </LookupConfigContext.Provider>
    </div>
  );
}

const styles = StyleSheet.create({
  columnTableContainer: {
    display: 'inline-block',
  },
  divTableRow: {
    ':hover': {
      background: 'white',
    },
    borderTop: 'none',
    padding: 0,
  },
});
